/**
 * PeopleTab — Members + Balances. (iter165: Sessions promoted to its own top-level
 * tab on GroupDetailPage, so the Sessions sub-filter was removed from here.)
 *
 * Two lightweight sub-views switched via filter chips. Default = members.
 */
import { useState, useMemo, useEffect } from 'react';
import { Users, Scale } from 'lucide-react';
import { TabsContent } from '../../components/ui/tabs';
import MembersTab from './MembersTab';
import BalancesTab from './BalancesTab';

export default function PeopleTab({ ctx }) {
  const filters = useMemo(() => ([
    { id: 'members',  label: 'Members',  Icon: Users },
    { id: 'balances', label: 'Balances', Icon: Scale },
  ]), []);

  // Deep-link: banner may still pass ?filter=members|balances.
  const initialFilter = ctx?.peopleInitialFilter;
  const [filter, setFilter] = useState(
    initialFilter && filters.some((f) => f.id === initialFilter) ? initialFilter : 'members'
  );
  useEffect(() => {
    if (initialFilter && filters.some((f) => f.id === initialFilter)) {
      setFilter(initialFilter);
    }
  }, [initialFilter, filters]);

  const activeFilter = filters.find((f) => f.id === filter) ? filter : 'members';

  return (
    <TabsContent value="people" className="space-y-3">
      <div
        className="flex gap-1.5 p-1 rounded-2xl bg-muted/60 border border-border"
        data-testid="people-filter-chips"
        role="tablist"
        aria-label="People filter"
      >
        {filters.map(({ id, label, Icon }) => {
          const active = activeFilter === id;
          return (
            <button
              key={id}
              data-testid={`people-filter-${id}`}
              role="tab"
              aria-selected={active}
              onClick={() => setFilter(id)}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold tracking-tight transition active:scale-[0.97] ${
                active
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
              {label}
            </button>
          );
        })}
      </div>

      {activeFilter === 'members'  && <MembersTab    ctx={ctx} />}
      {activeFilter === 'balances' && <BalancesTab   ctx={ctx} />}
    </TabsContent>
  );
}
